/**
 * ConsentGate — onboarding blocker (v2, with Laura's final copy).
 *
 * Required before the user can touch any part of the product:
 *   1) a country of residence (combobox with type-ahead search)
 *   2) FIVE non-preselected checkboxes (ToS, Privacy, Biometric,
 *      Rights declaration, AI Notice)
 *
 * Mirrors POST /api/consent to record each acceptance with
 * timestamp + IP + user-agent + document version server-side.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLocale } from "@/lib/locale";
import { buildCountryOptions } from "@/lib/countries";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { ExternalLink, Check, ChevronDown, Search, ShieldCheck } from "lucide-react";

const DOC_TYPES = [
    { key: "terms",              legalPath: "/legal/terms",             labelKey: "consent.v2_terms",     navKey: "legal.nav_terms" },
    { key: "privacy",            legalPath: "/legal/privacy",           labelKey: "consent.v2_privacy",   navKey: "legal.nav_privacy" },
    { key: "biometric",          legalPath: "/legal/biometric",         labelKey: "consent.v2_biometric", navKey: "legal.nav_biometric" },
    { key: "rights_declaration", legalPath: "/legal/memorial-policy",   labelKey: "consent.v2_rights",    navKey: "legal.nav_memorial" },
    { key: "ai_notice",          legalPath: "/legal/ai-notice",         labelKey: "consent.v2_ai_notice", navKey: "legal.nav_ai_notice" },
];

export default function ConsentGate({ onComplete }) {
    const { t, locale } = useLocale();
    const [country, setCountry] = useState("");
    const [checks, setChecks] = useState(
        DOC_TYPES.reduce((acc, d) => ({ ...acc, [d.key]: false }), {})
    );
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    // Country search combobox
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const comboRef = useRef(null);

    const options = useMemo(() => buildCountryOptions(locale || "en"), [locale]);
    const all = useMemo(
        () => [...options.preferred, ...options.rest],
        [options]
    );

    const selectedLabel = useMemo(() => {
        if (!country) return "";
        return (all.find((c) => c.code === country)?.name) || country;
    }, [country, all]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            // Show preferred first, then a compact first slice of rest
            return [...options.preferred, ...options.rest.slice(0, 40)];
        }
        return all.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                c.code.toLowerCase().includes(q)
        ).slice(0, 80);
    }, [query, all, options.preferred, options.rest]);

    // Close combobox on outside click
    useEffect(() => {
        const onDocClick = (e) => {
            if (comboRef.current && !comboRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const allChecked = DOC_TYPES.every((d) => checks[d.key]);
    const canSubmit = !!country && allChecked && !busy;

    const toggle = (k) => setChecks((prev) => ({ ...prev, [k]: !prev[k] }));

    const pickCountry = (code) => {
        setCountry(code);
        setQuery("");
        setOpen(false);
    };

    const submit = async () => {
        if (!canSubmit) {
            setErr(t("consent.err_missing"));
            return;
        }
        setBusy(true);
        setErr("");
        try {
            await api.post("/consent", {
                country,
                consents: DOC_TYPES.map((d) => ({
                    document_type: d.key,
                    country,
                    accepted: true,
                    context: "onboarding",
                })),
            });
            onComplete?.();
        } catch (_e) {
            setErr(t("consent.err_server"));
            setBusy(false);
        }
    };

    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <main className="max-w-2xl mx-auto px-6 md:px-12 pt-12 pb-24" data-testid="consent-gate">
                <div className="flex items-center gap-4 mb-10">
                    <BrandMark size={48} breathing={false} />
                    <div>
                        <p className="label-eyebrow text-ink-gold">{t("privacy.eyebrow")}</p>
                        <h1 className="h-serif text-4xl md:text-5xl mt-1">{t("consent.title_v2")}</h1>
                    </div>
                </div>

                <p className="text-ink-textMid text-lg mb-10 leading-relaxed max-w-xl">
                    {t("consent.body_v2")}
                </p>

                {/* ── Country — type-ahead combobox ───────────────────── */}
                <div className="mb-10" ref={comboRef}>
                    <label className="label-eyebrow" htmlFor="consent-country-btn">
                        {t("consent.country_label")}
                    </label>

                    <div className="relative mt-2">
                        <button
                            type="button"
                            id="consent-country-btn"
                            onClick={() => setOpen((o) => !o)}
                            className="w-full flex items-center justify-between input-line bg-ink-deep text-left"
                            aria-haspopup="listbox"
                            aria-expanded={open}
                            data-testid="consent-country-trigger"
                        >
                            <span className={selectedLabel ? "text-ink-textHigh" : "text-ink-textLow"}>
                                {selectedLabel || t("consent.country_placeholder")}
                            </span>
                            <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>

                        {open && (
                            <div
                                className="absolute left-0 right-0 top-full mt-2 z-30 bg-ink-deep border border-ink-border rounded-sm shadow-xl overflow-hidden"
                                data-testid="consent-country-popover"
                            >
                                <div className="flex items-center gap-2 px-3 py-2 border-b border-ink-border bg-ink-surface/50">
                                    <Search size={14} className="text-ink-textLow" aria-hidden="true" />
                                    <input
                                        autoFocus
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder={t("consent.country_search_ph")}
                                        className="flex-1 bg-transparent text-ink-textHigh placeholder:text-ink-textLow text-sm focus:outline-none py-1"
                                        data-testid="consent-country-search"
                                    />
                                </div>
                                <ul
                                    role="listbox"
                                    className="max-h-72 overflow-y-auto py-1"
                                    data-testid="consent-country-list"
                                >
                                    {filtered.length === 0 ? (
                                        <li className="px-3 py-2 text-sm text-ink-textLow italic">—</li>
                                    ) : filtered.map((c) => (
                                        <li key={c.code}>
                                            <button
                                                type="button"
                                                onClick={() => pickCountry(c.code)}
                                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                                                    country === c.code
                                                        ? "bg-ink-gold/15 text-ink-goldHi"
                                                        : "text-ink-textHigh hover:bg-ink-surface"
                                                }`}
                                                data-testid={`consent-country-option-${c.code}`}
                                            >
                                                <span>{c.name}</span>
                                                <span className="text-xs text-ink-textLow">{c.code}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <p className="text-sm text-ink-textMid italic mt-2">{t("consent.country_hint")}</p>
                </div>

                {/* ── Checkboxes — none pre-selected ───────────────────── */}
                <div className="space-y-5" data-testid="consent-checkboxes">
                    {DOC_TYPES.map((d) => (
                        <label
                            key={d.key}
                            className="flex items-start gap-4 cursor-pointer group"
                            data-testid={`consent-row-${d.key}`}
                        >
                            <input
                                type="checkbox"
                                checked={checks[d.key]}
                                onChange={() => toggle(d.key)}
                                className="mt-1 h-5 w-5 accent-ink-gold flex-shrink-0"
                                data-testid={`consent-check-${d.key}`}
                            />
                            <span className="flex-1">
                                <span className="block text-ink-textHigh leading-snug group-hover:text-ink-goldHi transition-colors">
                                    {t(d.labelKey)}
                                </span>
                                <Link
                                    to={d.legalPath}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 text-sm text-ink-gold hover:text-ink-goldHi mt-1"
                                    data-testid={`consent-link-${d.key}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {t(d.navKey)} <ExternalLink size={12} />
                                </Link>
                            </span>
                        </label>
                    ))}
                </div>

                {err && (
                    <p className="mt-8 text-ink-danger font-serif italic" data-testid="consent-error">{err}</p>
                )}

                <div className="mt-12 pt-8 border-t border-ink-border">
                    <button
                        onClick={submit}
                        disabled={!canSubmit}
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="consent-submit"
                    >
                        {busy ? t("consent.submitting") : t("consent.continue")}
                        {!busy && <Check size={16} />}
                    </button>
                    <p
                        className="mt-4 text-xs text-ink-textLow/90 tracking-wide inline-flex items-center gap-1.5"
                        data-testid="consent-trust"
                    >
                        <ShieldCheck size={12} className="text-ink-gold/70" aria-hidden="true" />
                        {t("consent.trust")}
                    </p>
                </div>
            </main>
        </div>
    );
}
